import * as React from "react";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";
import { useCombobox } from "downshift";
import { Storage } from "aws-amplify";
import { v1 as uuid } from "uuid";
import * as moment from "moment-timezone";

import { uint8ArrayToFile } from "src/library/utils/file";
import { Text } from "src/library/components/Text";
import {
  Input,
  Select,
  Radio
} from "src/library/components/Forms";
import { Button } from "src/library/components/Button";
import Grid from "src/library/components/Hedron";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  PromotionLinkType,
  usePromotionsApi,
  PromotionTemplate,
  PromotionsRequestName
} from "src/modules/promotions/api/promotions.api";
import usePromotions, { PromotionEditCreateState } from "src/modules/promotions/hooks/usePromotions";
import ProductSearchComponent from "src/core/products/components/ProductSearchComponent/ProductSearchComponent";
import { useSearchApi } from "src/core/shared/api/search.api";
import { ProductCategorySearchComponent } from "src/core/products/components/ProductCategories/ProductCategorySearch";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import {
  ISearchRequest,
  IProduct,
  IProductCategory,
  useProductsApi
} from "src/core/products/api/products.api";
import PromotionFormImage from "src/modules/promotions/components/PromotionFormImage";
import StartEndDateInput from "src/core/shared/components/Date/StartEndDateInput";
import generateListProductsParams from "src/core/products/utils/generate-list-products-params";

interface UrlParams {
  promotionId: string;
}

/**
 * Create promotion page
 */
const CreatePromotionPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = ({ title, ...props }) => {
  const { match, history } = props;
  const promotionId = match ? parseInt(match.params.promotionId, 10) : undefined;
  const checkPermission = useCheckUserPermission();
  const canAmendPromo = checkPermission("amend-promotion");

  const {
    isLoading, promotion, updatePromotionState
  } = usePromotions(defaultBannerTemplateConfig, promotionId);

  const [ imageUUID, setImageUUID ] = React.useState(uuid());
  const [ imageFile, setImageFile ] = React.useState<File | undefined>();
  const [ createPromotionRes, createPromotionReq ] = usePromotionsApi(PromotionsRequestName.CREATE_PROMOTION);
  const [ updatePromotionRes, updatePromotionReq ] = usePromotionsApi(PromotionsRequestName.UPDATE_PROMOTION);
  const [ getProductRes, getProductReq ] = useProductsApi("GET_PRODUCT");
  const [ getProductCatRes, getProductCatReq ] = useProductsApi("GET_PRODUCT_CATEGORY");
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_PRODUCTS");
  const [ selectedCategory, setSelectedCategory ] = React.useState<IProductCategory>();
  const [ saveLoading, setSavedLoading ] = React.useState(false);
  const [ mounted, setMounted ] = React.useState(false);

  const productDownShiftProps = useCombobox<IProduct>({
    items: listSearchResponse.data ? listSearchResponse.data.result : [],
    itemToString: () => "",
    onSelectedItemChange: changes => {
      if (changes.selectedItem) {
        updatePromotionState({ productId: changes.selectedItem.id });
      }
    }
  });

  /**
   * Load in the product or category on page load if required
   * */
  React.useEffect(() => {
    if (promotion && promotion.categoryId && !selectedCategory && !getProductCatRes.pending) {
      getProductCatReq(promotion.categoryId);
    } else if (promotion && promotion.productId && !productDownShiftProps.selectedItem && !getProductRes.pending) {
      getProductReq({ productId: promotion.productId });
    }
  }, [
    promotion,
    getProductCatRes,
    selectedCategory,
    getProductRes.pending,
    getProductCatReq,
    getProductReq
  ]);

  React.useEffect(() => {
    if (getProductCatRes.data) {
      setSelectedCategory(getProductCatRes.data);
    }
  }, [ getProductCatRes.data ]);

  React.useEffect(() => {
    if (getProductRes.data) {
      productDownShiftProps.selectItem(getProductRes.data);
    }
  }, [ getProductRes.data, productDownShiftProps ]);

  /**
   * Handle on save click
   */
  const handleOnSaveClick = React.useCallback(async () => {
    const imageDir = "assets/promotions";
    let result: {key: string} | undefined;

    // Check that the imageFile is set
    if (!imageFile) {
      toast.error("An image is a required. Please select an image.");

      return;
    }

    if (imageFile) {
      try {
        result = await Storage.put(`${imageDir}/${imageUUID}.${imageFile.type.split("/")[ 1 ]}`, imageFile, {
          level: "public",
          contentType: imageFile.type
        }) as {key: string};
      } catch (error) {
        toast.error("There was an error while uploading your image");

        throw error;
      }
    }

    if (promotion.id) {
      const updatedPromotion = {
        ...promotion,
        image: result ? result.key : promotion.image
      };

      delete updatedPromotion.previousState;

      updatePromotionReq({
        promotionId: promotion.id,
        data: {
          ...promotion,
          image: result ? result.key : promotion.image
        }
      });
    } else {
      if (!result || !result.key) {
        toast.error("An image is a required. Please select an image.");

        return;
      } else {
        const newPromotion = {
          ...promotion,
          image: result.key
        };

        delete newPromotion.previousState;

        createPromotionReq({
          ...promotion,
          image: result.key
        });
      }
    }
  }, [
    promotion,
    imageFile,
    createPromotionReq,
    updatePromotionReq
  ]);

  /**
   * Handles category selection
   */
  const handleCategorySelect = React.useCallback((category: IProductCategory) => {
    // Update the selectedCategory state
    setSelectedCategory(category);
    // Update the promotion state
    updatePromotionState({ categoryId: category.id });
  }, []);

  // set the imageFile if file valid
  const handleImageChange = React.useCallback((file: File) => {
    if (!file) {
      toast.error("An error has occurred while processing the selected image. Please try again.");
    } else {
      setImageFile(file);
    }
  }, []);

  // On promotion change
  React.useEffect(() => {
    // When editing an existing promotion, get the promotion image from S3 via Amplify
    if (typeof promotionId === "number" && !imageFile && promotion.image && !promotion.image.includes("http")) {
      // dont continue if template option has changed
      if (mounted && promotion.previousState?.template !== promotion.template) {
        return;
      }

      // used to pass template change check first time
      setMounted(true);

      Storage.get(promotion.image, {
        level: "public",
        download: true
      }).then(data => {
        const imageUrlSplit = promotion.image.split("/");
        const fileName = imageUrlSplit[ imageUrlSplit.length - 1 ];
        const fileNameSplit = fileName.split(".");

        // Remove the file extension element from the fileNameSplit
        fileNameSplit.pop();
        // Set the imageUUID
        setImageUUID(fileNameSplit.join("."));

        // Set the imageFile state based on the data return type
        switch (typeof data) {
          // if the data is an object
          case "object": {
            // @ts-ignore - AWS types this as a generic
            const buffer = data.Body as Uint8Array;
            // @ts-ignore - AWS types this as a generic
            const file = uint8ArrayToFile(buffer, fileName);

            // Set the imageFile state
            setImageFile(file);
            break;
          }

          default:
            // Log the error
            console.error("The Storage returned an unexpected data type");
            // Show an error message to the user
            toast.error("There was an error loading the promotion banner image. Please contact one of the administrators.");
        }
      }).catch(error => {
        // This catch stops page hard erroring in unlikely case image doesn't exist on existing promotion
        console.error(error);
        toast.error("Unable to fetch image. Please update image.");
      });
    }
  }, [ promotion, promotionId ]);

  // On createPromotionRes change
  React.useEffect(() => {
    setSavedLoading(createPromotionRes.pending);

    // If there's an error
    if (createPromotionRes.error) {
      // Show the error message
      toast.error(createPromotionRes.error.message);
    }

    // On success
    if (createPromotionRes.data) {
      // Show a success message
      toast.success("Your promotion banner was successfully saved.");
      // Move the user to the edit screen
      history.push(`/admin/promotions/edit/${createPromotionRes.data.id}`);
    }
  }, [
    createPromotionRes.error,
    createPromotionRes.data,
    createPromotionRes.pending
  ]);

  // On updatePromotionRes change
  React.useEffect(() => {
    setSavedLoading(updatePromotionRes.pending);

    // If there's an error
    if (updatePromotionRes.error) {
      // Show the error message
      toast.error(updatePromotionRes.error.message);
    }

    // On success
    if (updatePromotionRes.data) {
      // Show a success message
      toast.success("Your promotion banner was successfully saved.");
      updatePromotionState(updatePromotionRes.data);
    }
  }, [
    updatePromotionRes.error,
    updatePromotionRes.data,
    updatePromotionRes.pending
  ]);

  return (
    <Layout
      title={title}
      loading={isLoading || saveLoading}
      {...props}
    >
      {!isLoading && (
        <MainContentWrapper fill>
          <StyledCreatePromotion>
            <Text as="h4">
              Promotion Name:
            </Text>

            <Input
              as="input"
              name="name"
              autoHideLabels
              label=""
              value={promotion.name}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => updatePromotionState({ name: event.target.value })}
            />

            <div className="pt-lg">
              <Text as="h4">
                Template:
              </Text>

              <Radio
                options={[
                  {
                    text: "Large",
                    value: "1",
                    checked: promotion.template === PromotionTemplate.Large
                  },
                  {
                    text: "Small",
                    value: "0",
                    checked: promotion.template === PromotionTemplate.Small
                  }
                ]}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  updatePromotionState({ template: event.target.value === "1" ? PromotionTemplate.Large : PromotionTemplate.Small });
                  setImageFile(undefined);
                }}
                name="template"
                autoHideLabels
                label=""
                value={promotion.template === PromotionTemplate.Large ? "1" : "0"}
              />
            </div>

            <div className="pt-md">
              <Text as="h4">
                Link Image:
              </Text>

              <Text as="p">
                {`To ${promotion.image ? "change the" : "add an"} image, simply click or drag and drop onto the ${promotion.image ? "image" : "box"} below.`}
              </Text>

              <PromotionFormImage
                template={promotion.template}
                value={imageFile}
                handleImageChange={handleImageChange}
              />
            </div>

            <div className="pt-md link-input-items">
              <div className="link-type">
                <Text as="h4">
                  Link Type:
                </Text>

                <Select
                  as="select"
                  name="select-link-type"
                  autoHideLabels
                  label="Select link type"
                  value={promotion.linkType}
                  options={[
                    {
                      text: "Category",
                      value: PromotionLinkType.Category
                    },
                    {
                      text: "Product",
                      value: PromotionLinkType.Product
                    },
                    {
                      text: "URL",
                      value: PromotionLinkType.Raw
                    }
                  ]}
                  handleChange={(event: React.ChangeEvent<any>) => {
                    updatePromotionState({
                      linkType: event.target.value,
                      productId: undefined,
                      categoryId: undefined,
                      linkUrl: undefined
                    });
                    setSelectedCategory(undefined);
                    productDownShiftProps.reset();
                  }}
                />
              </div>

              <div className="link-type-option">
                {promotion.linkType === PromotionLinkType.Category && (
                  <>
                    <Text as="h4">
                      Link Category:
                    </Text>

                    {selectedCategory && (
                      <div className="selected-product">
                        <Text>
                          {selectedCategory.name}
                        </Text>
                      </div>
                    )}

                    <ProductCategorySearchComponent
                      onSelect={handleCategorySelect}
                      placeholder={`Search ${props.terminology.category.plural.text}...`}
                    />
                  </>
                )}

                {promotion.linkType === PromotionLinkType.Product && (
                  <>
                    <Text as="h4">
                      Link Product:
                    </Text>

                    {productDownShiftProps.selectedItem && (
                      <div className="selected-product">
                        <Text>
                          {`#${productDownShiftProps.selectedItem.partNumber} ${productDownShiftProps.selectedItem.description} `}
                        </Text>
                      </div>
                    )}

                    <ProductSearchComponent
                      error={listSearchResponse.error}
                      onSearch={(data: ISearchRequest) => getSearch(generateListProductsParams(data))}
                      loading={listSearchResponse.pending}
                      searchResults={listSearchResponse.data ? listSearchResponse.data.result : []}
                      downshiftProps={productDownShiftProps}
                      placeholder={`Search ${props.terminology.product.plural.text}...`}
                    />
                  </>
                )}

                {promotion.linkType === PromotionLinkType.Raw && (
                  <>
                    <Text as="h4">
                      Link URL:
                    </Text>

                    <Input
                      as="input"
                      name="link-url"
                      autoHideLabels
                      label="URL"
                      value={promotion.linkUrl}
                      handleChange={(event: React.ChangeEvent<HTMLInputElement>) => updatePromotionState({ linkUrl: event.target.value })}
                    />
                  </>
                )}
              </div>
            </div>

            <div className="pt-lg">
              <StartEndDateInput
                title="Promotions Live Period"
                dateRange={{
                  start: promotion.startDate,
                  end: promotion.endDate
                }}
                dateRangeUpdater={(type: "startDate" | "endDate", date: string) => updatePromotionState({ [ type ]: date })}
              />
            </div>

            {canAmendPromo && (
              <div className="submission-row">
                <Button
                  $buttonType="submit"
                  loading={isLoading || saveLoading}
                  onClick={handleOnSaveClick}
                >
                  Save
                </Button>
              </div>
            )}
          </StyledCreatePromotion>
        </MainContentWrapper>
      )}
    </Layout>
  );
};

export default CreatePromotionPage;

// The default banner template
const defaultBannerTemplateConfig: PromotionEditCreateState = {
  id: 0,
  name: "",
  template: PromotionTemplate.Large,
  image: "",
  linkType: PromotionLinkType.Raw,
  startDate: moment.utc().format("YYYY-MM-DD"),
  endDate: moment.utc().format("YYYY-MM-DD")
};

const StyledCreatePromotion = styled(Grid.Bounds)`
  ${({ theme }) => css`
    flex-direction: column;

    .pt-md {
      padding-top: ${theme.spacing.padding.md};
    }

    .pt-lg {
      padding-top: ${theme.spacing.padding.lg};
    }

    .link-input-items {
      display: flex;
      flex-direction: row;
    }

    .link-type {
      margin-top: 0;
      margin-right: ${theme.spacing.margins.lg};
    }

    .link-type-option {
      margin-top: 0;
      width: 35%;
    }

    .selected-product span {
      display: inline-block;
      padding: ${theme.spacing.padding.sm};
      margin: ${theme.spacing.margins.sm} 0;
      border-radius: ${theme.borderRadius};
      background: ${theme.colors.lightGrey};
    }

    .submission-row {
      padding-top: ${theme.spacing.padding.xl};
    }
  `};
`;
